import { InboxEntry } from '@this+that/models';
import { create } from 'zustand';

export enum DoBoxSelectionKind {
  TASK = 'task',
  SUGGESTION = 'suggestion',
}

export type DoBoxSelectionKindById = Record<string, DoBoxSelectionKind>;

export function getDoBoxSelectionKind(
  entry: Pick<InboxEntry, 'isSuggestion' | 'resolvedAt'>,
): DoBoxSelectionKind {
  return entry.isSuggestion === true && !entry.resolvedAt
    ? DoBoxSelectionKind.SUGGESTION
    : DoBoxSelectionKind.TASK;
}

export function buildDoBoxSelectionKindById(
  entries: InboxEntry[],
): DoBoxSelectionKindById {
  return Object.fromEntries(
    entries.map((e) => [e.id, getDoBoxSelectionKind(e)]),
  );
}

interface DoBoxMultiSelectState {
  selectedEntryIds: Set<string>;
  anchorId: string | null;
  orderedIds: string[];
  kindById: DoBoxSelectionKindById;
}

interface DoBoxMultiSelectActions {
  toggle: (id: string) => void;
  select: (id: string) => void;
  deselect: (id: string) => void;
  selectAll: (ids: string[]) => void;
  selectRange: (targetId: string) => void;
  setOrderedIds: (ids: string[], kindById: DoBoxSelectionKindById) => void;
  canSelect: (id: string) => boolean;
  clear: () => void;
}

interface DoBoxMultiSelectSelectors {
  hasSelection: boolean;
  selectedCount: number;
  selectionKind: DoBoxSelectionKind | null;
}

type DoBoxMultiSelectStore = DoBoxMultiSelectState &
  DoBoxMultiSelectActions &
  DoBoxMultiSelectSelectors;

function kindOf(
  kindById: DoBoxSelectionKindById,
  id: string,
): DoBoxSelectionKind {
  return kindById[id] ?? DoBoxSelectionKind.TASK;
}

const buildDerivedState = (
  ids: Set<string>,
  kindById: DoBoxSelectionKindById,
) => {
  const [first] = ids;
  return {
    hasSelection: ids.size > 0,
    selectedCount: ids.size,
    selectionKind: first === undefined ? null : kindOf(kindById, first),
  };
};

export function canSelectDoBoxEntry(
  state: Pick<DoBoxMultiSelectStore, 'selectionKind' | 'kindById'>,
  id: string,
): boolean {
  return (
    state.selectionKind === null ||
    kindOf(state.kindById, id) === state.selectionKind
  );
}

export const useDoBoxMultiSelectStore = create<DoBoxMultiSelectStore>(
  (set, get) => ({
    selectedEntryIds: new Set<string>(),
    anchorId: null,
    orderedIds: [],
    kindById: {},
    hasSelection: false,
    selectedCount: 0,
    selectionKind: null,

    canSelect: (id: string) => canSelectDoBoxEntry(get(), id),

    toggle: (id: string) => {
      set((state) => {
        const next = new Set(state.selectedEntryIds);
        if (next.has(id)) {
          next.delete(id);
        } else if (canSelectDoBoxEntry(state, id)) {
          next.add(id);
        } else {
          return state;
        }
        return {
          selectedEntryIds: next,
          anchorId: id,
          ...buildDerivedState(next, state.kindById),
        };
      });
    },

    select: (id: string) => {
      set((state) => {
        if (state.selectedEntryIds.has(id) || !canSelectDoBoxEntry(state, id)) {
          return state;
        }
        const next = new Set(state.selectedEntryIds);
        next.add(id);
        return {
          selectedEntryIds: next,
          ...buildDerivedState(next, state.kindById),
        };
      });
    },

    deselect: (id: string) => {
      set((state) => {
        if (!state.selectedEntryIds.has(id)) {
          return state;
        }
        const next = new Set(state.selectedEntryIds);
        next.delete(id);
        return {
          selectedEntryIds: next,
          ...buildDerivedState(next, state.kindById),
        };
      });
    },

    selectAll: (ids: string[]) => {
      set((state) => {
        const kind = state.selectionKind ?? DoBoxSelectionKind.TASK;
        const next = new Set(
          ids.filter((id) => kindOf(state.kindById, id) === kind),
        );
        return {
          selectedEntryIds: next,
          ...buildDerivedState(next, state.kindById),
        };
      });
    },

    setOrderedIds: (ids: string[], kindById: DoBoxSelectionKindById) => {
      set((state) => ({
        orderedIds: ids,
        kindById,
        ...buildDerivedState(state.selectedEntryIds, kindById),
      }));
    },

    selectRange: (targetId: string) => {
      set((state) => {
        const { anchorId, orderedIds, selectedEntryIds, kindById } = state;
        const anchorIndex =
          anchorId != null ? orderedIds.indexOf(anchorId) : -1;
        const targetIndex = orderedIds.indexOf(targetId);

        if (anchorId == null || anchorIndex === -1 || targetIndex === -1) {
          const next = new Set(selectedEntryIds);
          if (next.has(targetId)) {
            next.delete(targetId);
          } else if (canSelectDoBoxEntry(state, targetId)) {
            next.add(targetId);
          } else {
            return state;
          }
          return {
            selectedEntryIds: next,
            anchorId: targetId,
            ...buildDerivedState(next, kindById),
          };
        }

        const kind = state.selectionKind ?? kindOf(kindById, targetId);
        const min = Math.min(anchorIndex, targetIndex);
        const max = Math.max(anchorIndex, targetIndex);
        const next = new Set(selectedEntryIds);
        for (const id of orderedIds.slice(min, max + 1)) {
          if (kindOf(kindById, id) === kind) {
            next.add(id);
          }
        }
        return {
          selectedEntryIds: next,
          ...buildDerivedState(next, kindById),
        };
      });
    },

    clear: () => {
      const empty = new Set<string>();
      set((state) => ({
        selectedEntryIds: empty,
        anchorId: null,
        ...buildDerivedState(empty, state.kindById),
      }));
    },
  }),
);
